/**
 * Mastery badge — shows a sentence's progression through the 3-tier system:
 *   0 = NEW       看着学（提示全显示）
 *   1 = FAMILIAR  自己组（隐藏英文）
 *   2 = MASTERED  自己说（凭空回忆）
 *   3 = GODLIKE   毕业（不再推送）
 */
import { useAppStore } from '@/lib/store';

const TIERS = [
  { label: 'NEW', zh: '初见', color: 'text-cream-300 border-cream-300/40 bg-white/[0.04]' },
  { label: 'FAMILIAR', zh: '熟练', color: 'text-honey-300 border-honey-400/40 bg-honey-500/[0.08]' },
  { label: 'MASTERED', zh: '掌握', color: 'text-mint-300 border-mint-400/40 bg-mint-500/[0.08]' },
  { label: 'GODLIKE', zh: '出师', color: 'text-ember-300 border-ember-400/40 bg-ember-500/[0.08]' },
] as const;

interface Props {
  sentenceId: string;
  size?: 'sm' | 'md';
}

export function MasteryBadge({ sentenceId, size = 'sm' }: Props): JSX.Element {
  const lvl = useAppStore((s) => Math.min(3, s.mastery[sentenceId] ?? 0)) as 0 | 1 | 2 | 3;
  const tier = TIERS[lvl];
  const sizing = size === 'sm' ? 'text-[10px] px-1.5 py-0.5 gap-1' : 'text-xs px-2 py-1 gap-1.5';

  return (
    <span className={`inline-flex items-center rounded-full border font-arcade ${tier.color} ${sizing}`}>
      <span className="opacity-80">L{lvl}</span>
      <span>{tier.zh}</span>
    </span>
  );
}

export function MasteryDots({ sentenceId }: { sentenceId: string }): JSX.Element {
  const lvl = useAppStore((s) => Math.min(3, s.mastery[sentenceId] ?? 0));
  return (
    <span className="inline-flex gap-1">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className={`h-1 w-3 rounded-full transition-colors ${
            i < lvl ? 'bg-mint-400' : 'bg-white/[0.08]'
          }`}
        />
      ))}
    </span>
  );
}

export { TIERS };
